import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Card, 
  Button, 
  Space, 
  Table, 
  Input, 
  Modal, 
  message, 
  Tag, 
  Tooltip,
  Popconfirm,
  Checkbox,
  Row,
  Col,
  Statistic,
  Select,
  Form,
  Radio
} from 'antd';
import { 
  TagOutlined, 
  PlusOutlined, 
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  SearchOutlined
} from '@ant-design/icons';
import { tagsManagementService } from '../../services/tagsManagementService';

const { Title, Text } = Typography;
const { Option } = Select;

const TagsManagement = () => {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [form] = Form.useForm();

  // 筛选数据
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [topicNameLookups, setTopicNameLookups] = useState([]);
  const [topicHierarchy, setTopicHierarchy] = useState({ level1Topics: [], level2Topics: [] });
  
  // 方案一：知识库+主题筛选
  const [selectedKbId, setSelectedKbId] = useState('all');
  const [selectedTopicId, setSelectedTopicId] = useState('');
  const [topics, setTopics] = useState([]); // 新增：用于存储从kb_topic表获取的主题
  
  // 方案二：主题名称查找表筛选
  const [selectedIsPrivate, setSelectedIsPrivate] = useState('all');
  const [selectedLevel1TopicId, setSelectedLevel1TopicId] = useState('');
  const [selectedLevel2TopicId, setSelectedLevel2TopicId] = useState('');
  
  // 筛选方案选择
  const [filterScheme, setFilterScheme] = useState('kb-topic'); // 'kb-topic' 或 'tnl'

  // 获取知识库列表
  const fetchKnowledgeBases = async () => {
    try {
      const response = await tagsManagementService.getAllKnowledgeBases();
      setKnowledgeBases(response);
    } catch (error) {
      console.error('获取知识库列表失败:', error);
      message.error('Failed to fetch knowledge base list');
    }
  };

  // 获取主题名称查找表层级结构
  const fetchTopicHierarchy = async () => {
    try {
      const response = await tagsManagementService.getTopicNameLookupHierarchy();
      setTopicHierarchy(response);
    } catch (error) {
      console.error('获取主题名称查找表层级结构失败:', error);
      message.error('Failed to fetch topic hierarchy');
    }
  };

  // 获取标签列表
  const fetchTags = async () => {
    setLoading(true);
    try {
      let params = {
        page: currentPage,
        limit: pageSize
      };

      if (filterScheme === 'kb-topic') {
        // 方案一：知识库+主题筛选
        params = {
          ...params,
          kbId: selectedKbId,
          topicId: selectedTopicId
        };
      } else {
        // 方案二：主题名称查找表筛选
        params = {
          ...params,
          isPrivate: selectedIsPrivate !== 'all' ? selectedIsPrivate === 'true' : undefined,
          ...(selectedLevel1TopicId && { level1TopicId: selectedLevel1TopicId }),
          ...(selectedLevel2TopicId && { level2TopicId: selectedLevel2TopicId })
        };
      }

      const response = await tagsManagementService.getTags(params);
      
      setTags(response.items || []);
      setTotal(response.total || 0);
    } catch (error) {
      console.error('获取标签列表失败:', error);
      message.error('Failed to fetch tags');
    } finally {
      setLoading(false);
    }
  };

  // 获取kb_topic表中的主题列表
  const fetchTopics = async () => {
    if (selectedKbId === 'all') {
      setTopics([]);
      return;
    }
    try {
      const response = await tagsManagementService.getTopicsByKbId(selectedKbId);
      setTopics(response);
    } catch (error) {
      console.error('获取kb_topic主题列表失败:', error);
      message.error('Failed to fetch topics');
    }
  };

  // 组件加载时获取数据
  useEffect(() => {
    fetchKnowledgeBases();
    fetchTopicHierarchy();
  }, []);

  useEffect(() => {
    fetchTags();
    fetchTopics(); // 依赖于selectedKbId变化时重新获取主题列表
  }, [currentPage, pageSize, selectedKbId, selectedIsPrivate, selectedLevel1TopicId, selectedLevel2TopicId, filterScheme, selectedTopicId]);

  // 处理知识库选择变化
  const handleKbChange = (kbId) => {
    setSelectedKbId(kbId);
    setCurrentPage(1);
    setSelectedTopicId(''); // 清空主题选择
    setTopics([]); // 清空主题列表
  };

  // 处理分页变化
  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  // 处理添加标签
  const handleAdd = () => {
    if (filterScheme === 'kb-topic') {
      // 知识库+主题筛选方式：验证知识库和主题
      if (selectedKbId === 'all') {
        message.error('Please select a knowledge base first');
        return;
      }
      if (selectedTopicId === '') {
        message.error('Please select a topic first');
        return;
      }
    } else {
      // 主题名称查找表筛选方式：验证主题
      if (selectedLevel1TopicId === '' && selectedLevel2TopicId === '') {
        message.error('Please select a Level 1 or Level 2 topic first');
        return;
      }
    }
    
    setEditingTag(null);
    form.resetFields();
    setModalVisible(true);
  };

  // 处理编辑标签
  const handleEdit = (tag) => {
    setEditingTag(tag);
    form.setFieldsValue({
      tagname: tag.tagname,
      description: tag.description
    });
    setModalVisible(true);
  };

  // 处理删除标签
  const handleDelete = async (ttslId) => {
    try {
      const result = await tagsManagementService.deleteTag(ttslId);
      if (result.success) {
        message.success('Deleted successfully');
        fetchTags();
      } else {
        message.error(result.message || 'Delete failed');
      }
    } catch (error) {
      console.error('删除标签失败:', error);
      message.error('Delete failed');
    }
  };

  // 处理批量删除
  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Please select tags to delete');
      return;
    }

    setDeleteLoading(true);
    try {
      const result = await tagsManagementService.batchDeleteTags(selectedRowKeys);
      if (result.success) {
        message.success(`Batch delete successful: ${result.successCount} items`);
        setSelectedRowKeys([]);
        setDeleteModalVisible(false);
        fetchTags();
      } else {
        message.error(result.message || 'Batch delete failed');
      }
    } catch (error) {
      console.error('批量删除失败:', error);
      message.error('Batch delete failed');
    } finally {
      setDeleteLoading(false);
    }
  };

  // 处理模态框确认
  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      setModalLoading(true);

      if (editingTag) {
        // 编辑标签 - 只更新标签名称和描述
        await tagsManagementService.updateTag(editingTag.ttsl_id, {
          tagname: values.tagname,
          description: values.description
        });
        message.success('Tag updated successfully');
      } else {
        // 创建标签
        const createData = {
          tagname: values.tagname,
          description: values.description
        };
        
        if (filterScheme === 'kb-topic') {
          // 知识库+主题筛选方式：需要kb_id和topic_id（来自kb_topic表）
          createData.kb_id = selectedKbId !== 'all' ? selectedKbId : null;
          createData.topic_id = selectedTopicId;
          
          // 获取选中主题的tnl_id并设置到topic_tagname_sample_lookup.tnl_id
          if (selectedTopicId) {
            const selectedTopic = topics.find(topic => topic.topic_id === selectedTopicId);
            if (selectedTopic && selectedTopic.tnl_id) {
              createData.tnl_id = selectedTopic.tnl_id;
            }
          }
        } else {
          // 主题名称查找表筛选方式：需要tnl_id（来自topic_name_lookup表）
          const tnlId = selectedLevel2TopicId !== '' ? selectedLevel2TopicId : selectedLevel1TopicId;
          createData.tnl_id = tnlId;
        }
        
        await tagsManagementService.createTag(createData);
        message.success('Tag created successfully');
      }

      setModalVisible(false);
      fetchTags();
    } catch (error) {
      console.error('保存标签失败:', error);
      message.error('Save failed');
    } finally {
      setModalLoading(false);
    }
  };

  // 表格列定义
  const columns = [
    {
      title: 'Select',
      dataIndex: 'ttsl_id',
      width: 60,
      render: (id) => (
        <Checkbox 
          checked={selectedRowKeys.includes(id)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedRowKeys([...selectedRowKeys, id]);
            } else {
              setSelectedRowKeys(selectedRowKeys.filter(key => key !== id));
            }
          }}
        />
      )
    },
    {
      title: 'Tag Name',
      dataIndex: 'tagname',
      key: 'tagname',
      render: (tagname) => (
        <Tag color="blue" style={{ fontSize: '14px' }}>
          {tagname}
        </Tag>
      )
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (description) => (
        <Text type="secondary">
          {description || 'No description'}
        </Text>
      )
    },
    {
      title: 'Knowledge Base',
      dataIndex: 'kb_name',
      key: 'kb_name',
      render: (kbName) => (
        <Text strong>{kbName}</Text>
      )
    },
    {
      title: 'Topic',
      dataIndex: 'topic_name',
      key: 'topic_name',
      render: (topicName) => (
        <Text>{topicName}</Text>
      )
    },
    {
      title: 'TNL Topic',
      dataIndex: 'tnl_name',
      key: 'tnl_name',
      render: (tnlName) => (
        <Text>{tnlName}</Text>
      )
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => (
        <Text>{new Date(date).toLocaleString('en-US')}</Text>
      )
    },
    {
      title: 'Actions',
      key: 'action',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit tag">
            <Button 
              type="link" 
              icon={<EditOutlined />} 
              size="small"
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this tag?"
            onConfirm={() => handleDelete(record.ttsl_id)}
            okText="Confirm"
            cancelText="Cancel"
          >
            <Button 
              type="link" 
              danger 
              icon={<DeleteOutlined />} 
              size="small"
            />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div className="admin-module">
      <div className="admin-module-header">
        <Title level={3} className="admin-module-title">Tags Management</Title>
        <Text className="admin-module-description">
          Manage system tags, including adding, editing, and deleting tags
        </Text>
      </div>

      {/* 统计信息 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Tags"
              value={total}
              prefix={<TagOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Current Page"
              value={tags.length}
              prefix={<SearchOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Selected"
              value={selectedRowKeys.length}
              prefix={<DeleteOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Knowledge Bases"
              value={knowledgeBases.length}
              prefix={<TagOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* 筛选条件 */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 16 }}>
          <Radio.Group 
            value={filterScheme} 
            onChange={(e) => setFilterScheme(e.target.value)}
            style={{ marginBottom: 16 }}
          >
            <Radio.Button value="kb-topic">Knowledge Base + Topic Filter</Radio.Button>
            <Radio.Button value="tnl">Topic Name Lookup Filter</Radio.Button>
          </Radio.Group>
        </div>

        {filterScheme === 'kb-topic' ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
            {/* 知识库选择 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ whiteSpace: 'nowrap' }}>Knowledge Base:</span>
              <Select
                value={selectedKbId}
                onChange={handleKbChange}
                style={{ width: 200 }}
                placeholder="Select knowledge base"
              >
                <Option value="all">All</Option>
                {knowledgeBases.map(kb => (
                  <Option key={kb.kb_id} value={kb.kb_id}>
                    {kb.name}
                  </Option>
                ))}
              </Select>
            </div>
            {/* kb_topic主题选择 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ whiteSpace: 'nowrap' }}>Topic:</span>
              <Select
                value={selectedTopicId}
                onChange={(value) => setSelectedTopicId(value || '')}
                style={{ width: 200 }}
                placeholder="Select topic"
                disabled={selectedKbId === 'all'}
                allowClear
              >
                {/* 按层级结构显示主题 */}
                {(() => {
                  const level1Topics = topics.filter(topic => !topic.super_id);
                  const level2Topics = topics.filter(topic => topic.super_id);
                  
                  const result = [];
                  
                  // 先添加Level1主题
                  level1Topics.forEach(topic => {
                    result.push(
                      <Option key={topic.topic_id} value={topic.topic_id}>
                        {topic.name}
                      </Option>
                    );
                    
                    // 然后添加其下的Level2主题
                    const children = level2Topics.filter(child => child.super_id === topic.topic_id);
                    children.forEach(child => {
                      result.push(
                        <Option key={child.topic_id} value={child.topic_id}>
                          {'----'}{child.name}
                        </Option>
                      );
                    });
                  });
                  
                  return result;
                })()}
              </Select>
            </div>
            {/* 添加标签按钮 */}
            <Button
              type="primary"
              onClick={handleAdd}
              icon={<PlusOutlined />}
            >
              Add Tag
            </Button>
          </div>
        ) : (
          // 方案二：主题名称查找表筛选
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ whiteSpace: 'nowrap' }}>Private/Public:</span>
              <Select
                value={selectedIsPrivate}
                onChange={(value) => {
                  setSelectedIsPrivate(value);
                  setSelectedLevel1TopicId('');
                  setSelectedLevel2TopicId('');
                }}
                style={{ width: 120 }}
              >
                <Option value="all">All</Option>
                <Option value="true">Private</Option>
                <Option value="false">Public</Option>
              </Select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ whiteSpace: 'nowrap' }}>Level 1 Topic:</span>
              <Select
                value={selectedLevel1TopicId}
                onChange={(value) => {
                  setSelectedLevel1TopicId(value || '');
                  setSelectedLevel2TopicId('');
                }}
                style={{ width: 200 }}
                placeholder="Select Level 1 topic"
                allowClear
              >
                {topicHierarchy.level1Topics
                  .filter(topic => selectedIsPrivate === 'all' || String(topic.is_private) === selectedIsPrivate)
                  .map(topic => (
                    <Option key={topic.tnl_id} value={topic.tnl_id}>
                      {topic.name}
                    </Option>
                  ))}
              </Select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ whiteSpace: 'nowrap' }}>Level 2 Topic:</span>
              <Select
                value={selectedLevel2TopicId}
                onChange={(value) => setSelectedLevel2TopicId(value || '')}
                style={{ width: 200 }}
                placeholder="Select Level 2 topic"
                disabled={selectedLevel1TopicId === ''}
                allowClear
              >
                {topicHierarchy.level2Topics
                  .filter(topic => selectedLevel1TopicId === '' || topic.parent_name === topicHierarchy.level1Topics.find(t => t.tnl_id === selectedLevel1TopicId)?.name)
                  .map(topic => (
                    <Option key={topic.tnl_id} value={topic.tnl_id}>
                      {topic.name}
                    </Option>
                  ))}
              </Select>
            </div>
            {/* 添加标签按钮 */}
            <Button
              type="primary"
              onClick={handleAdd}
              icon={<PlusOutlined />}
            >
              Add Tag
            </Button>
          </div>
        )}

        <div style={{ marginTop: 16 }}>
          <Space>
            <Button
              onClick={() => {
                setSelectedRowKeys([]);
                fetchTags();
              }}
              icon={<ReloadOutlined />}
            >
              Refresh
            </Button>
          </Space>
        </div>
      </Card>

      {/* 操作按钮 */}
      <div style={{ marginBottom: 16 }}>
        <Space>
          <Button
            type="primary"
            danger
            disabled={selectedRowKeys.length === 0}
            onClick={() => setDeleteModalVisible(true)}
            icon={<DeleteOutlined />}
          >
            Batch Delete ({selectedRowKeys.length})
          </Button>
        </Space>
      </div>

      {/* 标签表格 */}
      <Table
        columns={columns}
        dataSource={tags}
        loading={loading}
        rowKey="ttsl_id"
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} records`,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
        onChange={handleTableChange}
        rowSelection={{
          selectedRowKeys,
          onChange: setSelectedRowKeys,
        }}
      />

      {/* 添加/编辑标签模态框 */}
      <Modal
        title={editingTag ? 'Edit Tag' : 'Add Tag'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => setModalVisible(false)}
        confirmLoading={modalLoading}
        okText="Confirm"
        cancelText="Cancel"
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            name="tagname"
            label="Tag Name"
            rules={[
              { required: true, message: 'Please enter tag name!' },
              { max: 50, message: 'Tag name cannot exceed 50 characters!' }
            ]}
          >
            <Input placeholder="Enter tag name" />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
          >
            <Input.TextArea placeholder="Enter tag description" rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 批量删除确认弹窗 */}
      <Modal
        title="Confirm Batch Delete"
        open={deleteModalVisible}
        onOk={handleBatchDelete}
        onCancel={() => setDeleteModalVisible(false)}
        confirmLoading={deleteLoading}
        okText="Confirm Delete"
        cancelText="Cancel"
      >
        <p>Are you sure you want to delete the selected {selectedRowKeys.length} tags? This action cannot be undone.</p>
      </Modal>
    </div>
  );
};

export default TagsManagement; 